<script lang="ts" setup>
  import isNil from 'lodash-es/isNil';
  import { computed, onMounted, ref, useAttrs, useCssModule, useSlots, watchEffect } from 'vue';

  import { convertDecimal, decimalSeparator, sanitizeDecimal } from '../../utils/i18n';
  import { FieldProps } from '../Field/Field.types';
  import Field from '../Field/Field.vue';
  import Icon from '../Icon/Icon.vue';

  export interface InputProps extends FieldProps {
    /**
     * Autocomplete takes in a string of off or on
     */
    autocomplete?: string;

    /**
     * Value for the input element.
     */
    modelValue?: string | number;

    /**
     * @deprecated Use :model-value or v-model instead of :value.
     */
    value?: string | number | null;

    /**
     * Input type. Excludes certain types that have a dedicated component.
     *
     * Note: For distinguishing between text & number internally, passing `number`
     * will still render an input with a type of `text` (for localization).
     */
    type?: string extends 'button' | 'checkbox' | 'radio' | 'submit' ? never : string;

    /**
     * Placeholder text for the input.
     * **Note:** placeholders should be used to display examples; they should not be used as labels because they are not accessible as labels. If a real label cannot be used, use the `aria-label` attribute.
     */
    placeholder?: string;
  }

  defineOptions({
    name: 'll-input',
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<InputProps>(), {
    autocomplete: 'off',
    type: 'text',
    modelValue: '',
    value: null,
    placeholder: undefined,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the input value changes.
     */
    (e: 'update:model-value', v: string | number): void;
    /**
     * Emitted when the input value changes.
     */
    (e: 'change', v: string | number): void;
    /**
     * Emitted when the input is focused
     */
    (e: 'focus', evt: Event): void;
    /**
     * Emitted when the input is blurred
     */
    (e: 'blur', evt: Event): void;
  }>();

  const slots = useSlots();
  const classes = useCssModule();
  const attrs = useAttrs();

  // declare a ref to hold the element reference
  // the name must match template ref value
  const inputEl = ref<HTMLInputElement>();

  // this exposes the input ref to any parent that renders the `Input` component
  // and gives it a template ref, giving parent components access to the DOM element.
  defineExpose({ inputEl });

  const internalValue = ref<string | number>(props.modelValue);

  if (props.type === 'number' && (props.modelValue || props.modelValue === 0)) {
    internalValue.value = convertDecimal(props.modelValue, decimalSeparator);
  }

  const showPassword = ref(false);

  const isReadOnly = computed(() => props.isReadOnly || ('readonly' in attrs && attrs.readonly !== false));
  const isNumber = computed(() => props.type === 'number');

  const inputAttrs = computed(() => {
    const tempAttrs = { ...attrs };

    delete tempAttrs.class;

    return tempAttrs;
  });

  const inputType = computed(() => {
    // type 'number' is a special use case and requires
    // that the type be 'text' to support internationalization
    if (isNumber.value) {
      return 'text';
    }

    if (props.type === 'password' && showPassword.value) {
      return 'text';
    }

    return props.type;
  });

  watchEffect(() => {
    internalValue.value = format(props.modelValue);
  });

  /**
   * Converts the localized number to a valid number (to emit). i.e. 12,50 > 12.50
   *
   * @param {string|number} value - Number you want to parse.
   * @returns {number|string} number or empty string (for empty input)
   */
  function parseNumber(value: string | number = '', isUserTyping = false): string | number {
    // If this isn't a number input, we shouldn't be calling this function
    if (!isNumber.value) {
      return value;
    }

    let tempValue = value;

    // If blank, just stop
    if (isNil(tempValue) || `${tempValue}`.length === 0) return '';

    // clean out different locale decimals
    if (decimalSeparator !== '.') {
      tempValue = convertDecimal(tempValue, '.');
    }

    // If the user is in the middle of an input, let them finish the number
    if (isUserTyping) {
      // Prefix values with a 0 if the user starts with a decimal point
      if (tempValue === '.') tempValue = '0.';

      // This is to prevent the parsing from correcting 0. to 0 and causing the value to be reset
      if (tempValue.toString().startsWith('0.')) return tempValue;
    }

    // Empty or null values convert to 0 with NumberFormat, so return '';
    if (!isNil(tempValue) && `${tempValue}`.length) {
      tempValue = Intl.NumberFormat('en-US', {
        style: 'decimal',
        maximumFractionDigits: 7,
        useGrouping: false,
      }).format(Number(tempValue));
    } else {
      return '';
    }

    // Ensure valid number, otherwise clear value
    return isNaN(Number(tempValue)) ? '' : parseFloat(tempValue);
  }

  /**
   * Formats the input based on conditions
   *
   * @param {number|string} value - The value to format.
   */
  function format(value: string | number) {
    if (isNumber.value) {
      return sanitizeDecimal(value, decimalSeparator);
    }

    return value;
  }

  /**
   * Fire after focusing out of input, if value has changed.
   */
  function handleChange() {
    // Parse the final value on blur
    const parsedValue = isNumber.value ? parseNumber(internalValue.value) : internalValue.value;
    emit('change', parsedValue);
  }

  /**
   * Fired when typing on input.
   */
  function handleInput(e: Event) {
    const value = (e.target as HTMLInputElement).value;

    // Update the internal value with a sanitized version
    internalValue.value = format(value);

    const parsedValue = isNumber.value ? parseNumber(internalValue.value, true) : value;

    emit('update:model-value', parsedValue);
  }

  onMounted(() => {
    if (props.value !== null) {
      throw new Error('ll-input: use :model-value or v-model instead of :value.');
    }

    if (attrs.onInput) {
      throw new Error('ll-input: use the @update:model-value event instead of @input');
    }
  });
</script>

<template>
  <Field v-bind="props" class="stash-input" :class="[classes.root, attrs.class]" data-test="ll-input">
    <template #default="{ fieldId, fieldErrorId, hasError }">
      <div class="relative">
        <input
          v-bind="inputAttrs"
          :id="fieldId"
          ref="inputEl"
          v-model="internalValue"
          :aria-errormessage="fieldErrorId"
          :aria-invalid="hasError"
          :autocomplete="autocomplete"
          :placeholder="props.placeholder"
          :class="[
            { [classes['input-prepended']]: !!slots.prepend },
            { [classes['input-appended']]: !!slots.append || props.type === 'password' },
            { [classes['has-error']]: !!props.errorText },
          ]"
          :type="inputType"
          :disabled="props.isDisabled || props.disabled"
          :readonly="isReadOnly"
          @change="handleChange"
          @input="handleInput"
          @blur="(evt) => emit('blur', evt)"
          @focus="(evt) => emit('focus', evt)"
        />

        <div
          v-if="slots.prepend"
          class="stash-input-prepend font-semibold"
          :class="[classes.symbol, classes['symbol--prepend']]"
        >
          <!-- @slot renders content on the left side of the input -->
          <slot name="prepend"> </slot>
        </div>

        <div
          v-if="slots.append || props.type === 'password'"
          class="stash-input-append font-semibold"
          :class="[classes.symbol, classes['symbol--append']]"
        >
          <!-- @slot renders content on the right side of the input -->
          <slot v-if="slots.append" name="append"></slot>
          <Icon
            v-else
            :name="showPassword ? 'hide' : 'show'"
            class="cursor-pointer"
            :class="{ 'text-ice-500': props.isDisabled || props.disabled }"
            :data-test="showPassword ? 'hide-password-icon' : 'show-password-icon'"
            @click="showPassword = !showPassword"
          />
        </div>
      </div>
    </template>
    <template v-if="slots.hint" #hint>
      <slot name="hint"></slot>
    </template>
  </Field>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .root {
      position: relative;
    }

    .root input {
      background: var(--color-white);
      border: 1px solid var(--color-ice-500);
      border-radius: var(--radius-sm);
      color: var(--color-ice-700);
      display: block;
      height: var(--height-input);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-normal);
      outline: none;
      padding: 0 --spacing(3);
      width: 100%;
    }

    .root input:hover {
      border-color: var(--color-ice-500);
    }

    .root input:active,
    .root input:focus {
      border-color: var(--color-blue-500);
    }

    .root input::placeholder {
      color: var(--color-ice-500);
      opacity: 1;
    }

    .root input.has-error:not(:focus) {
      border-color: var(--color-red-500);
    }

    .root input.input-prepended {
      padding-left: 36px;
    }

    .root input.input-appended {
      padding-right: 36px;
    }

    .symbol {
      align-items: center;
      display: flex;
      height: var(--height-input);
      justify-content: center;
      overflow: hidden;
      position: absolute;
      top: 0;
      width: var(--height-input);
    }

    .symbol--prepend {
      border-bottom-left-radius: var(--radius-sm);
      border-top-left-radius: var(--radius-sm);
      left: 0;
    }

    .symbol--append {
      border-bottom-right-radius: var(--radius-sm);
      border-top-right-radius: var(--radius-sm);
      right: 0;
    }

    .root input[disabled] {
      background-color: var(--color-ice-100) !important;
      border-color: var(--color-ice-500) !important;
      color: var(--color-ice-500) !important;
      pointer-events: none;
      outline-color: var(--color-ice-500) !important;

      & + .symbol--prepend,
      & + .symbol--append {
        color: var(--color-ice-500);
      }
    }

    .root input[readonly] {
      border-color: transparent;
      background-color: transparent;
      padding-left: 0;
      padding-right: 0;
    }

    .root input[disabled]:active,
    .root input[readonly]:active,
    .root input[disabled]:focus,
    .root input[readonly]:focus {
      box-shadow: none !important;
    }

    .root input[disabled]::placeholder {
      text-transform: none;
    }
  }
</style>
